/**
 * CheckboxField.tsx
 *
 * Unified checkbox/toggle field component
 * Handles: checkbox, yes-no-toggle
 */

import React from 'react';
import { BaseFieldProps } from './shared/types';
import { FieldWrapper } from './shared';
import { Switch } from '../ui/switch';

export const CheckboxField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
}) => {
  const isPreviewMode = mode === 'preview';
  const isDisabled = disabled || isPreviewMode;

  const handleChange = (checked: boolean) => {
    if (isPreviewMode) return;
    onChange(fieldConfig.id, checked);
  };

  const isLabelRight = fieldConfig.layout?.labelPosition === 'right';
  const alignment = fieldConfig.layout?.alignment || 'left';

  const alignmentClass =
    alignment === 'center'
      ? 'justify-center'
      : alignment === 'right'
      ? 'justify-end'
      : 'justify-start';

  const switchElement = (
    <div className={`flex items-center ${isLabelRight ? 'flex-row-reverse' : 'flex-row'} gap-2 ${alignmentClass}`}>
      <Switch
        id={fieldConfig.id}
        checked={Boolean(value) || false}
        onCheckedChange={handleChange}
        disabled={isDisabled}
      />
      {/* Hide inline label in preview mode since it's shown in the field header */}
      {!isPreviewMode && (
        <label
          htmlFor={fieldConfig.id}
          className="cursor-pointer"
        >
          {fieldConfig.label}{' '}
          {fieldConfig.required && <span className="text-red-500">*</span>}
        </label>
      )}
    </div>
  );

  return (
    <FieldWrapper
      field={fieldConfig}
      errors={errors}
      hasError={hasError}
      showLabel={false} // We render the label inline with the switch
      isPreview={isPreviewMode}
    >
      {switchElement}
    </FieldWrapper>
  );
});

CheckboxField.displayName = 'CheckboxField';
